import { t } from "@lingui/macro";
import { Button, Input, Label } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { CheckCircle2, Plus, SkipForward } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { type educationInfoType, useAudioChatStore } from "@/client/stores/audioChatStore";

import {
  recordLegacyEvent,
  recordLegacyMessage,
  recordLegacyMessages,
  recordLegacyStateUpdate,
} from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
  showIllustration?: boolean;
};
type messageType = Message[];
type EducationField = keyof educationInfoType;
type EducationChatProps = {
  focusTextarea: () => void;
  scrollToBottom: () => void;
};

const educationPromptId = "education-repeatable-form";

const emptyEducation: educationInfoType = {
  degree: "",
  duration: "",
  institute: "",
  major: "",
};

const hasEducationContent = (education: educationInfoType) =>
  Boolean(
    education.degree.trim() ||
      education.duration.trim() ||
      education.institute.trim() ||
      education.major.trim(),
  );

const countFilledEducationEntries = (educationInfo: educationInfoType[]) =>
  educationInfo.filter(hasEducationContent).length;

export const EducationChat = ({ focusTextarea, scrollToBottom }: EducationChatProps) => {
  const [educationJd, setEducationJd] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [messages, setMessages] = useState<messageType>([]);
  const [educationError, setEducationError] = useState("");
  const step = useAudioChatStore((state) => state.step);
  const setStep = useAudioChatStore((state) => state.setStep);
  const chatTextData = useAudioChatStore((state) => state.chatTextData);
  const setChatTextData = useAudioChatStore((state) => state.setChatTextData);
  const educationInfo = useAudioChatStore((state) => state.educationInfo);
  const updateEducationInfoDegree = useAudioChatStore((state) => state.updateEducationInfoDegree);
  const updateEducationInfoDuration = useAudioChatStore(
    (state) => state.updateEducationInfoDuration,
  );
  const updateEducationInfoInstitute = useAudioChatStore(
    (state) => state.updateEducationInfoInstitute,
  );
  const updateEducationInfoMajor = useAudioChatStore((state) => state.updateEducationInfoMajor);
  const updateEducationInfAddNewEdu = useAudioChatStore(
    (state) => state.updateEducationInfAddNewEdu,
  );

  const visibleEducationInfo = useMemo(
    () => (educationInfo.length > 0 ? educationInfo : [emptyEducation]),
    [educationInfo],
  );
  const filledEducationCount = countFilledEducationEntries(educationInfo);

  const educationFields: Array<{
    field: EducationField;
    label: string;
    placeholder: string;
    autoComplete?: string;
  }> = [
    {
      field: "degree",
      label: t`Degree or qualification`,
      placeholder: t`Bachelor of Computer Science`,
    },
    {
      field: "institute",
      label: t`Institution`,
      placeholder: t`RMIT University`,
      autoComplete: "organization",
    },
    {
      field: "major",
      label: t`Major or focus`,
      placeholder: t`Software Engineering`,
    },
    {
      field: "duration",
      label: t`Dates`,
      placeholder: t`2019 - 2023`,
    },
  ];

  const pushMessages = useCallback(
    (mes: Message) => {
      setMessages((prevMessages) => [...prevMessages, mes]);
      recordLegacyMessage(mes);
      scrollToBottom();
    },
    [scrollToBottom],
  );

  const updateEducationField = (index: number, field: EducationField, value: string) => {
    switch (field) {
      case "degree":
        updateEducationInfoDegree(index, value);
        break;
      case "duration":
        updateEducationInfoDuration(index, value);
        break;
      case "institute":
        updateEducationInfoInstitute(index, value);
        break;
      case "major":
        updateEducationInfoMajor(index, value);
        break;
    }

    setEducationError("");
    recordLegacyStateUpdate(`education.${index}.${field}`, value);
  };

  const handleAddEducation = () => {
    const lastEducation = visibleEducationInfo[visibleEducationInfo.length - 1];
    if (lastEducation && !hasEducationContent(lastEducation)) {
      setEducationError(t`Add details to the current education entry first, or skip this section.`);
      return;
    }

    updateEducationInfAddNewEdu();
    recordLegacyEvent({
      type: "state_updated",
      path: "education.added",
      value: visibleEducationInfo.length,
    });
  };

  const submitEducation = (skip = false) => {
    const educationEntryCount = skip ? 0 : filledEducationCount;

    recordLegacyEvent({
      type: "prompt_submitted",
      promptId: educationPromptId,
      value: {
        skipped: skip,
        education: skip ? [] : educationInfo.filter(hasEducationContent),
      },
    });

    if (educationEntryCount > 0) {
      pushMessages({
        type: "user",
        content: t`Added ${educationEntryCount} education entries.`,
      });
      pushMessages({
        type: "ai",
        content: t`Got it. I'll include those education details in your resume.`,
      });
    } else {
      pushMessages({
        type: "user",
        content: t`Skipped education`,
      });
      pushMessages({
        type: "ai",
        content: t`No problem. We can build the resume without education details.`,
      });
    }

    setIsSubmitted(true);
    setEducationError("");
    setStep("TEMPLATES");
  };

  const handleChatFallback = (input: string) => {
    const trimmedInput = input.trim();
    const targetIndex = visibleEducationInfo.findIndex((education) => !education.degree.trim());
    const nextIndex = targetIndex >= 0 ? targetIndex : visibleEducationInfo.length;

    if (nextIndex === visibleEducationInfo.length) {
      updateEducationInfAddNewEdu();
    }

    updateEducationInfoDegree(nextIndex, trimmedInput);
    recordLegacyStateUpdate(`education.${nextIndex}.degree`, trimmedInput);

    pushMessages({
      type: "user",
      content: trimmedInput,
    });
    pushMessages({
      type: "ai",
      content: t`I added that as a qualification. You can complete the rest below.`,
    });

    setChatTextData("");
  };

  useEffect(() => {
    if (step === "EDU") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Add your education details, or skip this section if it is not relevant for the role.`,
          showIllustration: true,
        },
      ];
      setEducationJd(true);
      setIsSubmitted(false);
      setEducationError("");
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
      recordLegacyEvent({ type: "prompt_shown", promptId: educationPromptId });
      focusTextarea();
    }
  }, [step]);

  useEffect(() => {
    if (step === "EDU" && chatTextData) {
      handleChatFallback(chatTextData);
    }
  }, [chatTextData]);

  return (
    <>
      {educationJd &&
        messages.map((message, index) => (
          <div key={`edu-${index}`}>
            <div
              className={cn(
                "relative mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
                message.type === "user"
                  ? "ml-auto bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-800",
              )}
            >
              {message.type === "ai" ? (
                <div className="flex items-center gap-2">
                  <div className="grow">
                    <TypingText message={message.content} />
                  </div>

                  {message.showIllustration && (
                    <div className="size-32 shrink-0">
                      <img
                        alt=""
                        className="float-right h-full scale-125 object-cover object-center"
                        src="/backgrounds/educationChat.svg"
                      />
                    </div>
                  )}
                </div>
              ) : (
                message.content
              )}
            </div>
          </div>
        ))}

      {educationJd && step === "EDU" && !isSubmitted && (
        <div className="mx-1 mb-5 w-[min(100%,48rem)] rounded-lg border border-border bg-background p-4 shadow-sm">
          <div className="mb-4 flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">{t`Education`}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t`Add degrees, certificates, bootcamps, or training that strengthens this application.`}
            </p>
          </div>

          <div className="space-y-4">
            {visibleEducationInfo.map((education, index) => {
              const entryNumber = index + 1;

              return (
                <section
                  key={`education-${index}`}
                  aria-label={t`Education entry ${entryNumber}`}
                  className="rounded-md border border-border bg-secondary/20 p-3"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-foreground">
                      {t`Education ${entryNumber}`}
                    </h4>
                    {!hasEducationContent(education) && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{t`Optional`}</span>
                    )}
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {educationFields.map(({ field, label, placeholder, autoComplete }) => {
                      const fieldId = `education-${index}-${field}`;

                      return (
                        <div key={field} className="min-w-0 space-y-1.5">
                          <Label htmlFor={fieldId} className="text-xs font-medium text-foreground">
                            {label}
                          </Label>
                          <Input
                            id={fieldId}
                            value={education[field]}
                            autoComplete={autoComplete}
                            placeholder={placeholder}
                            onChange={(event) =>
                              updateEducationField(index, field, event.target.value)
                            }
                            className="!h-10 !rounded-md !border bg-background text-sm shadow-none focus-visible:ring-2 focus-visible:ring-primary/30"
                          />
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>

          {educationError && (
            <p className="mt-3 text-xs font-medium text-error">{educationError}</p>
          )}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="md"
                className="gap-2"
                onClick={handleAddEducation}
              >
                <Plus size={16} aria-hidden="true" />
                {t`Add another`}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                className="gap-2"
                onClick={() => submitEducation(true)}
              >
                <SkipForward size={16} aria-hidden="true" />
                {t`Skip education`}
              </Button>
            </div>

            <Button type="button" size="md" className="gap-2" onClick={() => submitEducation()}>
              <CheckCircle2 size={16} aria-hidden="true" />
              {t`Continue`}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};
